import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useBlocker } from 'react-router-dom'
import axios from 'axios'
import {
  Settings, Save, Trash2, AlertCircle, CheckCircle,
  Loader2, Calculator, Euro, Percent, TrendingUp, Check, Users, Crown,
  Brain, FolderCog, Package, Plus, Search, X, Truck, Ban, Info
} from 'lucide-react'
import { familyInstructionsApi } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

// ============================================================================
// TYPES
// ============================================================================

interface PricingTier {
  min: number
  max: number
  margin_percent: number
}

interface FamilyPricingConfig {
  vat_rate: number
  tiers_pvp: PricingTier[]
  tiers_pvd: PricingTier[]
  tiers_vip: PricingTier[]
}

interface PriceResult {
  cost_price: number
  tariff: string
  margin_percent: number
  net_price_theoretical: number
  price_with_vat_theoretical: number
  price_with_vat_rounded: number
  final_net_price: number
}

interface MultiPriceResult {
  pvp: PriceResult
  pvd: PriceResult
  vip: PriceResult
}

type TariffType = 'pvp' | 'pvd' | 'vip'

interface PriceException {
  reference: string
  product_name: string
  margin_pvp: number
  margin_pvd: number
  margin_vip: number
}

interface FamilyProduct {
  reference: string
  name: string
}

interface StockException {
  reference: string
  product_name: string
  weeks: number
  min_stock?: number
  max_stock?: number
}

interface ExcludedFamily {
  family_code: string
  family_name: string
  reason: string
}

interface ExcludedFamilies {
  pricing_excluded: ExcludedFamily[]
  stock_excluded: ExcludedFamily[]
}

// Default pricing config for new families
const DEFAULT_PRICING_CONFIG: FamilyPricingConfig = {
  vat_rate: 21,
  tiers_pvp: [{ min: 0, max: 50, margin_percent: 40 }, { min: 50, max: 200, margin_percent: 30 }, { min: 200, max: 99999, margin_percent: 20 }],
  tiers_pvd: [{ min: 0, max: 50, margin_percent: 30 }, { min: 50, max: 200, margin_percent: 22 }, { min: 200, max: 99999, margin_percent: 15 }],
  tiers_vip: [{ min: 0, max: 50, margin_percent: 25 }, { min: 50, max: 200, margin_percent: 18 }, { min: 200, max: 99999, margin_percent: 12 }],
}

export default function ConfigurationPage() {
  const toast = useToast()
  const queryClient = useQueryClient()

  // Main tabs
  const [activeMainTab, setActiveMainTab] = useState<'pricing' | 'ai' | 'stock'>('pricing')

  // Selected family
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null)

  // Pricing state
  const [activeTariffTab, setActiveTariffTab] = useState<TariffType>('pvp')
  const [familyPricingConfigs, setFamilyPricingConfigs] = useState<Record<string, FamilyPricingConfig>>({})
  const [testCost, setTestCost] = useState<string>('')
  const [testResult, setTestResult] = useState<MultiPriceResult | null>(null)
  const [isPricingDirty, setIsPricingDirty] = useState(false)
  const [isEditingVat, setIsEditingVat] = useState(false)

  // AI Instructions state
  const [editedInstructions, setEditedInstructions] = useState<Record<string, string>>({})
  const [saveStatus, setSaveStatus] = useState<Record<string, 'saving' | 'saved' | 'error'>>({})

  // Stock minimum state
  const [familyStockWeeks, setFamilyStockWeeks] = useState<Record<string, number>>({})
  const [stockSaveStatus, setStockSaveStatus] = useState<Record<string, 'saving' | 'saved' | 'error'>>({})

  // Price exceptions state
  const [showExceptionForm, setShowExceptionForm] = useState(false)
  const [exceptionSearch, setExceptionSearch] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<FamilyProduct | null>(null)
  const [exceptionMargins, setExceptionMargins] = useState({ pvp: 0, pvd: 0, vip: 0 })

  // Stock exceptions state
  const [showStockExceptionForm, setShowStockExceptionForm] = useState(false)
  const [stockExceptionSearch, setStockExceptionSearch] = useState('')
  const [selectedStockProduct, setSelectedStockProduct] = useState<FamilyProduct | null>(null)
  const [stockExceptionWeeks, setStockExceptionWeeks] = useState(0)

  // Block navigation if dirty
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isPricingDirty && currentLocation.pathname !== nextLocation.pathname
  )

  useEffect(() => {
    if (blocker.state === "blocked") {
      const confirm = window.confirm("Tienes cambios sin guardar. ¿Seguro que quieres salir?")
      if (confirm) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isPricingDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isPricingDirty])

  // ============================================================================
  // QUERIES
  // ============================================================================

  // Get families with instructions
  const familiesQuery = useQuery({
    queryKey: ['families-with-instructions'],
    queryFn: async () => {
      const result = await familyInstructionsApi.getFamiliesWithInstructions()
      return result.families
    },
  })

  // Get family pricing configs
  const pricingConfigsQuery = useQuery({
    queryKey: ['family-pricing-configs'],
    queryFn: async () => {
      const res = await axios.get('/api/pricing/family-configs')
      return res.data as Record<string, FamilyPricingConfig>
    }
  })

  // Get global pricing config (fallback/default)
  const globalPricingQuery = useQuery({
    queryKey: ['pricingConfig'],
    queryFn: async () => {
      const res = await axios.get('/api/pricing/config')
      return res.data as FamilyPricingConfig
    }
  })

  // Get family stock configs
  const stockConfigsQuery = useQuery({
    queryKey: ['family-stock-configs'],
    queryFn: async () => {
      const res = await axios.get('/api/family-config/stock-weeks')
      return res.data as Record<string, number>
    }
  })

  // Get excluded families
  const excludedFamiliesQuery = useQuery({
    queryKey: ['excluded-families'],
    queryFn: async () => {
      const res = await axios.get('/api/family-config/excluded-families')
      return res.data as ExcludedFamilies
    }
  })

  // Get price exceptions for selected family
  const priceExceptionsQuery = useQuery({
    queryKey: ['price-exceptions', selectedFamily],
    queryFn: async () => {
      if (!selectedFamily) return {}
      const res = await axios.get(`/api/family-config/price-exceptions/${selectedFamily}`)
      return res.data as Record<string, PriceException>
    },
    enabled: !!selectedFamily
  })

  // Search products in family (for price exceptions)
  const productSearchQuery = useQuery({
    queryKey: ['family-products', selectedFamily, exceptionSearch],
    queryFn: async () => {
      if (!selectedFamily || exceptionSearch.length < 2) return { products: [] }
      const res = await axios.get(`/api/family-config/products/${selectedFamily}`, {
        params: { q: exceptionSearch, limit: 20 }
      })
      return res.data as { success: boolean, products: FamilyProduct[] }
    },
    enabled: !!selectedFamily && exceptionSearch.length >= 2
  })

  // Get stock exceptions for selected family
  const stockExceptionsQuery = useQuery({
    queryKey: ['stock-exceptions', selectedFamily],
    queryFn: async () => {
      if (!selectedFamily) return {}
      const res = await axios.get(`/api/family-config/stock-exceptions/${selectedFamily}`)
      return res.data as Record<string, StockException>
    },
    enabled: !!selectedFamily
  })

  // Search products in family (for stock exceptions)
  const stockProductSearchQuery = useQuery({
    queryKey: ['family-products-stock', selectedFamily, stockExceptionSearch],
    queryFn: async () => {
      if (!selectedFamily || stockExceptionSearch.length < 2) return { products: [] }
      const res = await axios.get(`/api/family-config/products/${selectedFamily}`, {
        params: { q: stockExceptionSearch, limit: 20 }
      })
      return res.data as { success: boolean, products: FamilyProduct[] }
    },
    enabled: !!selectedFamily && stockExceptionSearch.length >= 2
  })

  // Initialize stock configs when data loads
  useEffect(() => {
    if (stockConfigsQuery.data) {
      setFamilyStockWeeks(stockConfigsQuery.data)
    }
  }, [stockConfigsQuery.data])

  // Initialize pricing configs when data loads
  useEffect(() => {
    if (pricingConfigsQuery.data) {
      setFamilyPricingConfigs(pricingConfigsQuery.data)
    }
  }, [pricingConfigsQuery.data])

  // Initialize instructions when family is selected
  useEffect(() => {
    if (selectedFamily && familiesQuery.data) {
      const family = familiesQuery.data.find(f => f.code === selectedFamily)
      if (family && !(selectedFamily in editedInstructions)) {
        setEditedInstructions(prev => ({
          ...prev,
          [selectedFamily]: family.instructions || ''
        }))
      }
    }
  }, [selectedFamily, familiesQuery.data])

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  // Save family pricing config
  const savePricingMutation = useMutation({
    mutationFn: async ({ familyCode, config }: { familyCode: string, config: FamilyPricingConfig }) => {
      await axios.post(`/api/pricing/family-configs/${familyCode}`, config)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-pricing-configs'] })
      setIsPricingDirty(false)
      toast.success('Configuracion de precios guardada')
    },
    onError: (err) => {
      console.error(err)
      toast.error('Error al guardar configuracion de precios')
    }
  })

  // Calculate prices mutation
  const calculateMutation = useMutation({
    mutationFn: async ({ cost, familyCode }: { cost: number, familyCode: string }) => {
      const res = await axios.post('/api/pricing/calculate-family', { cost_price: cost, family_code: familyCode })
      return res.data as MultiPriceResult
    },
    onSuccess: (data) => {
      setTestResult(data)
    }
  })

  // Save AI instructions
  const saveInstructionsMutation = useMutation({
    mutationFn: (data: { family_code: string; family_name: string; instructions: string }) =>
      familyInstructionsApi.save(data),
    onMutate: (data) => {
      setSaveStatus(prev => ({ ...prev, [data.family_code]: 'saving' }))
    },
    onSuccess: (_, data) => {
      setSaveStatus(prev => ({ ...prev, [data.family_code]: 'saved' }))
      queryClient.invalidateQueries({ queryKey: ['families-with-instructions'] })
      setTimeout(() => {
        setSaveStatus(prev => {
          const newStatus = { ...prev }
          delete newStatus[data.family_code]
          return newStatus
        })
      }, 2000)
    },
    onError: (_, data) => {
      setSaveStatus(prev => ({ ...prev, [data.family_code]: 'error' }))
    },
  })

  // Delete AI instructions
  const deleteInstructionsMutation = useMutation({
    mutationFn: (familyCode: string) => familyInstructionsApi.delete(familyCode),
    onSuccess: (_, familyCode) => {
      queryClient.invalidateQueries({ queryKey: ['families-with-instructions'] })
      setEditedInstructions(prev => {
        const newEdited = { ...prev }
        delete newEdited[familyCode]
        return newEdited
      })
    },
  })

  // Save stock weeks config
  const saveStockWeeksMutation = useMutation({
    mutationFn: async ({ familyCode, weeks }: { familyCode: string, weeks: number }) => {
      await axios.post(`/api/family-config/stock-weeks/${familyCode}`, { weeks })
    },
    onMutate: ({ familyCode }) => {
      setStockSaveStatus(prev => ({ ...prev, [familyCode]: 'saving' }))
    },
    onSuccess: (_, { familyCode }) => {
      setStockSaveStatus(prev => ({ ...prev, [familyCode]: 'saved' }))
      queryClient.invalidateQueries({ queryKey: ['family-stock-configs'] })
      setTimeout(() => {
        setStockSaveStatus(prev => {
          const newStatus = { ...prev }
          delete newStatus[familyCode]
          return newStatus
        })
      }, 2000)
    },
    onError: (_, { familyCode }) => {
      setStockSaveStatus(prev => ({ ...prev, [familyCode]: 'error' }))
    },
  })

  // Save price exception
  const savePriceExceptionMutation = useMutation({
    mutationFn: async ({ familyCode, exception }: { familyCode: string, exception: PriceException }) => {
      await axios.post(`/api/family-config/price-exceptions/${familyCode}`, exception)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-exceptions', selectedFamily] })
      setShowExceptionForm(false)
      setSelectedProduct(null)
      setExceptionSearch('')
      setExceptionMargins({ pvp: 0, pvd: 0, vip: 0 })
    },
  })

  // Delete price exception
  const deletePriceExceptionMutation = useMutation({
    mutationFn: async ({ familyCode, reference }: { familyCode: string, reference: string }) => {
      await axios.delete(`/api/family-config/price-exceptions/${familyCode}/${encodeURIComponent(reference)}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-exceptions', selectedFamily] })
    },
  })

  // Save stock exception
  const saveStockExceptionMutation = useMutation({
    mutationFn: async ({ familyCode, exception }: { familyCode: string, exception: StockException }) => {
      await axios.post(`/api/family-config/stock-exceptions/${familyCode}`, exception)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-exceptions', selectedFamily] })
      setShowStockExceptionForm(false)
      setSelectedStockProduct(null)
      setStockExceptionSearch('')
      setStockExceptionWeeks(0)
    },
  })

  // Delete stock exception
  const deleteStockExceptionMutation = useMutation({
    mutationFn: async ({ familyCode, reference }: { familyCode: string, reference: string }) => {
      await axios.delete(`/api/family-config/stock-exceptions/${familyCode}/${encodeURIComponent(reference)}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-exceptions', selectedFamily] })
    },
  })

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const getCurrentFamilyConfig = (): FamilyPricingConfig => {
    if (!selectedFamily) return globalPricingQuery.data || DEFAULT_PRICING_CONFIG
    return familyPricingConfigs[selectedFamily] || globalPricingQuery.data || DEFAULT_PRICING_CONFIG
  }

  const handleTierChange = (index: number, field: keyof PricingTier, value: string) => {
    if (!selectedFamily) return

    const currentConfig = getCurrentFamilyConfig()
    const tierKey = `tiers_${activeTariffTab}` as keyof FamilyPricingConfig
    const currentTiers = [...(currentConfig[tierKey] as PricingTier[])]
    currentTiers[index] = { ...currentTiers[index], [field]: parseFloat(value) || 0 }

    setFamilyPricingConfigs(prev => ({
      ...prev,
      [selectedFamily]: { ...currentConfig, [tierKey]: currentTiers }
    }))
    setIsPricingDirty(true)
  }

  const addTier = () => {
    if (!selectedFamily) return
    const currentConfig = getCurrentFamilyConfig()
    const tierKey = `tiers_${activeTariffTab}` as keyof FamilyPricingConfig
    const currentTiers = currentConfig[tierKey] as PricingTier[]
    const lastMax = currentTiers.length > 0 ? currentTiers[currentTiers.length - 1].max : 0
    const newTier: PricingTier = { min: lastMax, max: lastMax + 100, margin_percent: 20 }

    setFamilyPricingConfigs(prev => ({
      ...prev,
      [selectedFamily]: { ...currentConfig, [tierKey]: [...currentTiers, newTier] }
    }))
    setIsPricingDirty(true)
  }

  const removeTier = (index: number) => {
    if (!selectedFamily) return
    const currentConfig = getCurrentFamilyConfig()
    const tierKey = `tiers_${activeTariffTab}` as keyof FamilyPricingConfig
    const currentTiers = (currentConfig[tierKey] as PricingTier[]).filter((_, i) => i !== index)

    setFamilyPricingConfigs(prev => ({
      ...prev,
      [selectedFamily]: { ...currentConfig, [tierKey]: currentTiers }
    }))
    setIsPricingDirty(true)
  }

  const handleVatChange = (value: string) => {
    if (!selectedFamily) return
    const currentConfig = getCurrentFamilyConfig()
    setFamilyPricingConfigs(prev => ({
      ...prev,
      [selectedFamily]: { ...currentConfig, vat_rate: parseFloat(value) || 0 }
    }))
    setIsPricingDirty(true)
  }

  const handleSavePricing = () => {
    if (!selectedFamily) return
    const config = familyPricingConfigs[selectedFamily]
    if (config) {
      savePricingMutation.mutate({ familyCode: selectedFamily, config })
    }
  }

  const handleTest = (e: React.FormEvent) => {
    e.preventDefault()
    if (testCost && selectedFamily) {
      calculateMutation.mutate({ cost: parseFloat(testCost), familyCode: selectedFamily })
    }
  }

  const handleSaveInstructions = () => {
    if (!selectedFamily) return
    const family = familiesQuery.data?.find(f => f.code === selectedFamily)
    const instructions = editedInstructions[selectedFamily]?.trim()
    if (!instructions || !family) return

    saveInstructionsMutation.mutate({
      family_code: selectedFamily,
      family_name: family.name,
      instructions,
    })
  }

  const handleDeleteInstructions = () => {
    if (!selectedFamily) return
    if (confirm('¿Eliminar las indicaciones para esta familia?')) {
      deleteInstructionsMutation.mutate(selectedFamily)
    }
  }

  const handleSaveStockWeeks = () => {
    if (!selectedFamily) return
    const weeks = familyStockWeeks[selectedFamily] ?? 0
    saveStockWeeksMutation.mutate({ familyCode: selectedFamily, weeks })
  }

  const handleSavePriceException = () => {
    if (!selectedFamily || !selectedProduct) return
    savePriceExceptionMutation.mutate({
      familyCode: selectedFamily,
      exception: {
        reference: selectedProduct.reference,
        product_name: selectedProduct.name,
        margin_pvp: exceptionMargins.pvp,
        margin_pvd: exceptionMargins.pvd,
        margin_vip: exceptionMargins.vip
      }
    })
  }

  const handleDeletePriceException = (reference: string) => {
    if (!selectedFamily) return
    if (confirm(`¿Eliminar la excepcion de precios para ${reference}?`)) {
      deletePriceExceptionMutation.mutate({ familyCode: selectedFamily, reference })
    }
  }

  const handleSelectProduct = (product: FamilyProduct) => {
    setSelectedProduct(product)
    setExceptionSearch('')
  }

  const resetExceptionForm = () => {
    setShowExceptionForm(false)
    setSelectedProduct(null)
    setExceptionSearch('')
    setExceptionMargins({ pvp: 0, pvd: 0, vip: 0 })
  }

  // Stock exception handlers
  const handleSaveStockException = () => {
    if (!selectedFamily || !selectedStockProduct) return
    saveStockExceptionMutation.mutate({
      familyCode: selectedFamily,
      exception: {
        reference: selectedStockProduct.reference,
        product_name: selectedStockProduct.name,
        weeks: stockExceptionWeeks
      }
    })
  }

  const handleDeleteStockException = (reference: string) => {
    if (!selectedFamily) return
    if (confirm(`¿Eliminar la excepcion de stock para ${reference}?`)) {
      deleteStockExceptionMutation.mutate({ familyCode: selectedFamily, reference })
    }
  }

  const handleSelectStockProduct = (product: FamilyProduct) => {
    setSelectedStockProduct(product)
    setStockExceptionSearch('')
  }

  const resetStockExceptionForm = () => {
    setShowStockExceptionForm(false)
    setSelectedStockProduct(null)
    setStockExceptionSearch('')
    setStockExceptionWeeks(0)
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  const families = familiesQuery.data || []
  const isLoading = familiesQuery.isLoading || pricingConfigsQuery.isLoading || globalPricingQuery.isLoading
  const selectedFamilyData = families.find(f => f.code === selectedFamily)
  const currentConfig = getCurrentFamilyConfig()
  const activeTiers = currentConfig[`tiers_${activeTariffTab}`] || []

  // Check if a family is excluded from pricing/stock
  const excludedFamilies = excludedFamiliesQuery.data || { pricing_excluded: [], stock_excluded: [] }
  const isPricingExcluded = (code: string) => excludedFamilies.pricing_excluded.some(e => e.family_code === code)
  const isStockExcluded = (code: string) => excludedFamilies.stock_excluded.some(e => e.family_code === code)
  const getPricingExclusionReason = (code: string) => excludedFamilies.pricing_excluded.find(e => e.family_code === code)?.reason
  const getStockExclusionReason = (code: string) => excludedFamilies.stock_excluded.find(e => e.family_code === code)?.reason
  const selectedFamilyPricingExcluded = selectedFamily ? isPricingExcluded(selectedFamily) : false
  const selectedFamilyStockExcluded = selectedFamily ? isStockExcluded(selectedFamily) : false

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
          <p className="text-slate-500 font-medium">Cargando configuracion...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
          <Settings className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </span>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Familias</h1>
          <p className="text-slate-600 dark:text-slate-400">Configura precios, indicaciones IA y stock minimo por familia</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Family Selector (Left Column) */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <FolderCog className="w-5 h-5 text-purple-500" />
              Familias
            </h2>
            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {families.map((family) => {
                const isSelected = selectedFamily === family.code
                const hasPricingConfig = !!familyPricingConfigs[family.code]
                const hasStockConfig = familyStockWeeks[family.code] !== undefined && familyStockWeeks[family.code] > 0
                const familyPricingExcluded = isPricingExcluded(family.code)
                const familyStockExcluded = isStockExcluded(family.code)
                const isExcluded = familyPricingExcluded || familyStockExcluded
                return (
                  <button
                    key={family.code}
                    onClick={() => setSelectedFamily(family.code)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                        : isExcluded
                        ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-700 dark:text-slate-300 border border-red-200 dark:border-red-800'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs text-slate-500 dark:text-slate-400 shrink-0">
                          {family.code}
                        </span>
                        <span className="truncate text-sm">{family.name}</span>
                        {isExcluded && (
                          <Ban className="w-3.5 h-3.5 text-red-500 shrink-0" title="Familia excluida" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {hasPricingConfig && !familyPricingExcluded && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full" title="Precios configurados" />
                        )}
                        {family.has_instructions && (
                          <span className="w-2 h-2 bg-success-500 rounded-full" title="Indicaciones IA configuradas" />
                        )}
                        {hasStockConfig && !familyStockExcluded && (
                          <span className="w-2 h-2 bg-orange-500 rounded-full" title="Stock minimo configurado" />
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Leyenda:</p>
              <div className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>Precios configurados</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-success-500 rounded-full" />
                  <span>Indicaciones IA</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span>Stock minimo</span>
                </div>
                <div className="flex items-center gap-2">
                  <Ban className="w-3 h-3 text-red-500" />
                  <span>Excluida del sistema</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration Panel (Right Column) */}
        <div className="lg:col-span-3">
          {!selectedFamily ? (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-12 text-center">
              <FolderCog className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Selecciona una familia
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Elige una familia del panel izquierdo para configurar sus margenes de precios e indicaciones IA
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Family Header */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-slate-500 dark:text-slate-400">Configurando familia:</span>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      <span className="font-mono text-purple-600 dark:text-purple-400 mr-2">{selectedFamily}</span>
                      {selectedFamilyData?.name}
                    </h2>
                  </div>
                </div>
              </div>

              {/* Exclusion Warning Banner */}
              {(selectedFamilyPricingExcluded || selectedFamilyStockExcluded) && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <Ban className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2">
                        Familia Excluida del Sistema Automatico
                      </h3>
                      <div className="space-y-2 text-sm text-red-700 dark:text-red-300">
                        {selectedFamilyPricingExcluded && (
                          <div className="flex items-center gap-2">
                            <Euro className="w-4 h-4" />
                            <span>
                              <strong>Precios:</strong> {getPricingExclusionReason(selectedFamily!) || 'Excluida del calculo automatico de precios'}
                            </span>
                          </div>
                        )}
                        {selectedFamilyStockExcluded && (
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            <span>
                              <strong>Stock:</strong> {getStockExclusionReason(selectedFamily!) || 'Excluida del calculo automatico de stock minimo'}
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="mt-3 text-xs text-red-600 dark:text-red-400">
                        Los productos de esta familia requieren configuracion manual. Las configuraciones de margenes y stock minimo no se aplicaran automaticamente.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Tabs */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                  <div className="flex">
                    <button
                      onClick={() => setActiveMainTab('pricing')}
                      className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 ${
                        activeMainTab === 'pricing'
                          ? 'border-blue-600 text-primary-600 bg-white dark:bg-slate-800'
                          : 'border-transparent text-slate-500 hover:text-gray-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Euro className="w-5 h-5" />
                      Margenes de Precios
                    </button>
                    <button
                      onClick={() => setActiveMainTab('ai')}
                      className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 ${
                        activeMainTab === 'ai'
                          ? 'border-green-600 text-success-600 bg-white dark:bg-slate-800'
                          : 'border-transparent text-slate-500 hover:text-gray-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Brain className="w-5 h-5" />
                      Imperativos IA
                    </button>
                    <button
                      onClick={() => setActiveMainTab('stock')}
                      className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 ${
                        activeMainTab === 'stock'
                          ? 'border-orange-600 text-orange-600 bg-white dark:bg-slate-800'
                          : 'border-transparent text-slate-500 hover:text-gray-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Package className="w-5 h-5" />
                      Stock Minimo
                    </button>
                  </div>
                </div>

                {/* Pricing Tab Content */}
                {activeMainTab === 'pricing' && (
                  <div className="p-6">
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                      {/* Configuration Column */}
                      <div className="xl:col-span-2 space-y-6">
                        {/* Global Settings */}
                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-primary-500" />
                            Ajustes Generales
                          </h3>
                          <div className="flex items-center gap-4">
                            <div className="w-40">
                              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                                IVA (%)
                              </label>
                              <div className="flex items-center gap-2">
                                <div className="relative flex-1">
                                  <input
                                    type="number"
                                    value={currentConfig.vat_rate}
                                    onChange={(e) => handleVatChange(e.target.value)}
                                    disabled={!isEditingVat}
                                    className={`w-full pl-3 pr-8 py-2 rounded-lg border text-sm ${
                                      isEditingVat
                                        ? 'bg-white dark:bg-slate-900 border-blue-500'
                                        : 'bg-slate-100 dark:bg-slate-800 border-transparent cursor-not-allowed font-semibold'
                                    } text-slate-900 dark:text-white`}
                                  />
                                  <Percent className="absolute right-2 top-2 w-4 h-4 text-slate-400" />
                                </div>
                                <button
                                  onClick={() => setIsEditingVat(!isEditingVat)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    isEditingVat
                                      ? 'bg-success-100 text-success-600 hover:bg-green-200'
                                      : 'bg-blue-50 text-primary-600 hover:bg-blue-100'
                                  }`}
                                >
                                  {isEditingVat ? <Check className="w-4 h-4" /> : <Euro className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Tariff Tabs */}
                        <div>
                          <div className="flex space-x-2 mb-4">
                            <button
                              onClick={() => setActiveTariffTab('pvp')}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                                activeTariffTab === 'pvp'
                                  ? 'bg-blue-100 dark:bg-blue-900/50 text-primary-600 border border-blue-300'
                                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                              }`}
                            >
                              <Users className="w-4 h-4" /> PVP (Publico)
                            </button>
                            <button
                              onClick={() => setActiveTariffTab('pvd')}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                                activeTariffTab === 'pvd'
                                  ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 border border-purple-300'
                                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                              }`}
                            >
                              <Truck className="w-4 h-4" /> PVD (Distribuidor)
                            </button>
                            <button
                              onClick={() => setActiveTariffTab('vip')}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                                activeTariffTab === 'vip'
                                  ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 border border-amber-300'
                                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                              }`}
                            >
                              <Crown className="w-4 h-4" /> VIP
                            </button>
                          </div>

                          {/* Tiers Table */}
                          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                            <div className="grid grid-cols-12 gap-3 mb-3 px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              <div className="col-span-4">Coste Min (EUR)</div>
                              <div className="col-span-4">Coste Max (EUR)</div>
                              <div className="col-span-3">Margen (%)</div>
                              <div className="col-span-1"></div>
                            </div>

                            <div className="space-y-2">
                              {activeTiers.map((tier, index) => (
                                <div
                                  key={index}
                                  className="grid grid-cols-12 gap-3 items-center p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                                >
                                  <div className="col-span-4 relative">
                                    <input
                                      type="number"
                                      value={tier.min}
                                      onChange={(e) => handleTierChange(index, 'min', e.target.value)}
                                      className="w-full pl-6 py-1.5 bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                                    />
                                    <span className="absolute left-2 top-2 text-slate-400 text-sm">€</span>
                                  </div>
                                  <div className="col-span-4 relative">
                                    <input
                                      type="number"
                                      value={tier.max}
                                      onChange={(e) => handleTierChange(index, 'max', e.target.value)}
                                      className="w-full pl-6 py-1.5 bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                                    />
                                    <span className="absolute left-2 top-2 text-slate-400 text-sm">€</span>
                                  </div>
                                  <div className="col-span-3 relative">
                                    <input
                                      type="number"
                                      value={tier.margin_percent}
                                      onChange={(e) => handleTierChange(index, 'margin_percent', e.target.value)}
                                      className="w-full pl-3 pr-6 py-1.5 font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm"
                                    />
                                    <Percent className="absolute right-2 top-2 w-3 h-3 text-blue-400" />
                                  </div>
                                  <div className="col-span-1 flex justify-end">
                                    <button
                                      onClick={() => removeTier(index)}
                                      className="p-1.5 text-red-400 hover:text-danger-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <button
                              onClick={addTier}
                              className="mt-3 w-full py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-500 hover:text-primary-600 hover:border-blue-300 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                            >
                              + Anadir Nuevo Tramo
                            </button>
                          </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end">
                          <button
                            onClick={handleSavePricing}
                            disabled={savePricingMutation.isPending || !isPricingDirty}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-white shadow transition-all ${
                              savePricingMutation.isPending
                                ? 'bg-gray-400 cursor-not-allowed'
                                : savePricingMutation.isSuccess && !isPricingDirty
                                  ? 'bg-green-600'
                                  : isPricingDirty
                                    ? 'bg-primary-600 hover:bg-primary-700'
                                    : 'bg-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {savePricingMutation.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : savePricingMutation.isSuccess && !isPricingDirty ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                            {savePricingMutation.isSuccess && !isPricingDirty ? 'Guardado!' : 'Guardar Precios'}
                          </button>
                        </div>

                        {/* Price Exceptions Section */}
                        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                              <AlertCircle className="w-5 h-5 text-amber-500" />
                              Excepciones de Precios
                            </h3>
                            {!showExceptionForm && (
                              <button
                                onClick={() => setShowExceptionForm(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors font-medium text-sm"
                              >
                                <Plus className="w-4 h-4" />
                                Anadir Excepcion
                              </button>
                            )}
                          </div>

                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                            Las excepciones permiten definir margenes personalizados para referencias especificas, ignorando los tramos de la familia.
                          </p>

                          {/* Add Exception Form */}
                          {showExceptionForm && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="font-medium text-amber-800 dark:text-amber-200">Nueva Excepcion</h4>
                                <button
                                  onClick={resetExceptionForm}
                                  className="p-1 text-slate-500 hover:text-gray-700 dark:hover:text-slate-300"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>

                              {/* Product Search */}
                              {!selectedProduct ? (
                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Buscar producto en familia {selectedFamily}
                                  </label>
                                  <div className="relative">
                                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                    <input
                                      type="text"
                                      value={exceptionSearch}
                                      onChange={(e) => setExceptionSearch(e.target.value)}
                                      placeholder="Escribe al menos 2 caracteres para buscar..."
                                      className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                    />
                                    {productSearchQuery.isLoading && (
                                      <Loader2 className="absolute right-3 top-2.5 w-4 h-4 text-primary-500 animate-spin" />
                                    )}
                                  </div>

                                  {/* Search Results */}
                                  {productSearchQuery.data?.products && productSearchQuery.data.products.length > 0 && (
                                    <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800">
                                      {productSearchQuery.data.products.map((product) => (
                                        <button
                                          key={product.reference}
                                          onClick={() => handleSelectProduct(product)}
                                          className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                        >
                                          <span className="font-mono text-xs text-purple-600 dark:text-purple-400">{product.reference}</span>
                                          <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">{product.name}</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}

                                  {exceptionSearch.length >= 2 && productSearchQuery.data?.products?.length === 0 && !productSearchQuery.isLoading && (
                                    <p className="text-sm text-slate-500 dark:text-slate-400 py-2">No se encontraron productos</p>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {/* Selected Product */}
                                  <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                                    <div>
                                      <span className="font-mono text-sm text-purple-600 dark:text-purple-400">{selectedProduct.reference}</span>
                                      <p className="text-sm text-slate-700 dark:text-slate-300">{selectedProduct.name}</p>
                                    </div>
                                    <button
                                      onClick={() => setSelectedProduct(null)}
                                      className="p-1 text-slate-400 hover:text-danger-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* Margin Inputs */}
                                  <div className="grid grid-cols-3 gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                                        <Users className="w-3 h-3 text-primary-500" /> Margen PVP (%)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={exceptionMargins.pvp}
                                        onChange={(e) => setExceptionMargins(prev => ({ ...prev, pvp: parseFloat(e.target.value) || 0 }))}
                                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                                        <Truck className="w-3 h-3 text-purple-500" /> Margen PVD (%)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={exceptionMargins.pvd}
                                        onChange={(e) => setExceptionMargins(prev => ({ ...prev, pvd: parseFloat(e.target.value) || 0 }))}
                                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                                        <Crown className="w-3 h-3 text-amber-500" /> Margen VIP (%)
                                      </label>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={exceptionMargins.vip}
                                        onChange={(e) => setExceptionMargins(prev => ({ ...prev, vip: parseFloat(e.target.value) || 0 }))}
                                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                      />
                                    </div>
                                  </div>

                                  {/* Save Exception Button */}
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={resetExceptionForm}
                                      className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      onClick={handleSavePriceException}
                                      disabled={savePriceExceptionMutation.isPending}
                                      className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium"
                                    >
                                      {savePriceExceptionMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Save className="w-4 h-4" />
                                      )}
                                      Guardar Excepcion
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Existing Exceptions List */}
                          {priceExceptionsQuery.data && Object.keys(priceExceptionsQuery.data).length > 0 ? (
                            <div className="space-y-2">
                              {Object.values(priceExceptionsQuery.data).map((exception) => (
                                <div
                                  key={exception.reference}
                                  className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm text-purple-600 dark:text-purple-400">{exception.reference}</span>
                                      <span className="text-sm text-slate-600 dark:text-slate-400">{exception.product_name}</span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-1 text-xs">
                                      <span className="text-primary-600 dark:text-primary-400">PVP: {exception.margin_pvp}%</span>
                                      <span className="text-purple-600 dark:text-purple-400">PVD: {exception.margin_pvd}%</span>
                                      <span className="text-amber-600 dark:text-amber-400">VIP: {exception.margin_vip}%</span>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeletePriceException(exception.reference)}
                                    disabled={deletePriceExceptionMutation.isPending}
                                    className="p-2 text-red-400 hover:text-danger-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No hay excepciones configuradas para esta familia</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Simulator Column */}
                      <div className="xl:col-span-1">
                        <div className="sticky top-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl shadow-xl p-5">
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Calculator className="w-5 h-5 text-blue-400" />
                            Simulador
                          </h3>

                          <form onSubmit={handleTest} className="space-y-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Precio de Coste
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={testCost}
                                  onChange={(e) => setTestCost(e.target.value)}
                                  className="w-full pl-8 pr-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-white placeholder-gray-400 font-medium"
                                  placeholder="0.00"
                                />
                                <span className="absolute left-3 top-3 text-slate-400">€</span>
                              </div>
                            </div>

                            <button
                              type="submit"
                              disabled={!testCost || calculateMutation.isPending}
                              className="w-full py-2.5 bg-primary-600 hover:bg-blue-500 disabled:bg-gray-600 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                            >
                              {calculateMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Calculator className="w-4 h-4" />
                              )}
                              Calcular
                            </button>
                          </form>

                          {testResult && (
                            <div className="mt-6 space-y-3">
                              {/* PVP */}
                              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-blue-300 font-semibold flex items-center gap-1 text-sm">
                                    <Users className="w-3 h-3" /> PVP
                                  </span>
                                  <span className="text-xs bg-blue-900/50 text-blue-200 px-2 py-0.5 rounded">
                                    {testResult.pvp.margin_percent}%
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="text-xl font-bold text-white">
                                    {testResult.pvp.price_with_vat_rounded.toFixed(2)}€
                                  </span>
                                  <span className="block text-[10px] text-slate-400 mt-1">
                                    IVA incl. · Redondeado a ,99
                                  </span>
                                  <span className="block text-[10px] text-slate-500">
                                    Neto: {testResult.pvp.net_price_theoretical.toFixed(2)}€ (sin IVA)
                                  </span>
                                </div>
                              </div>

                              {/* PVD */}
                              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-purple-300 font-semibold flex items-center gap-1 text-sm">
                                    <Truck className="w-3 h-3" /> PVD
                                  </span>
                                  <span className="text-xs bg-purple-900/50 text-purple-200 px-2 py-0.5 rounded">
                                    {testResult.pvd.margin_percent}%
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="text-xl font-bold text-white">
                                    {testResult.pvd.price_with_vat_rounded.toFixed(2)}€
                                  </span>
                                  <span className="block text-[10px] text-slate-400 mt-1">
                                    IVA incl. · Sin redondeo
                                  </span>
                                  <span className="block text-[10px] text-slate-500">
                                    Neto: {testResult.pvd.net_price_theoretical.toFixed(2)}€ (sin IVA)
                                  </span>
                                </div>
                              </div>

                              {/* VIP */}
                              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-amber-300 font-semibold flex items-center gap-1 text-sm">
                                    <Crown className="w-3 h-3" /> VIP
                                  </span>
                                  <span className="text-xs bg-amber-900/50 text-amber-200 px-2 py-0.5 rounded">
                                    {testResult.vip.margin_percent}%
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="text-xl font-bold text-white">
                                    {testResult.vip.price_with_vat_rounded.toFixed(2)}€
                                  </span>
                                  <span className="block text-[10px] text-slate-400 mt-1">
                                    IVA incl. · Sin redondeo
                                  </span>
                                  <span className="block text-[10px] text-slate-500">
                                    Neto: {testResult.vip.net_price_theoretical.toFixed(2)}€ (sin IVA)
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Instructions Tab Content */}
                {activeMainTab === 'ai' && (
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <Brain className="w-6 h-6 text-success-600 dark:text-success-400 shrink-0 mt-0.5" />
                          <div>
                            <h3 className="font-semibold text-green-800 dark:text-green-200">Imperativos IA</h3>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                              Estas instrucciones se aplicaran automaticamente a cualquier creacion o edicion de productos
                              de esta familia. Son de obligado cumplimiento para la IA.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Instrucciones para la familia <span className="font-mono text-purple-600">{selectedFamily}</span>
                        </label>
                        <textarea
                          value={editedInstructions[selectedFamily] ?? selectedFamilyData?.instructions ?? ''}
                          onChange={(e) =>
                            setEditedInstructions(prev => ({
                              ...prev,
                              [selectedFamily]: e.target.value
                            }))
                          }
                          placeholder="Escribe aqui las instrucciones para OpenAI cuando genere productos de esta familia...

Ejemplo: 'Esta familia incluye pantallas LCD. Asegurate de incluir siempre la resolucion, el tamano en pulgadas y el tipo de conector. Menciona la compatibilidad con modelos de portatiles si esta disponible.'"
                          className="w-full h-64 p-4 text-sm border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white resize-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-500">
                            {(editedInstructions[selectedFamily] ?? '').length} caracteres
                          </span>
                          <div className="flex items-center gap-2">
                            {saveStatus[selectedFamily] === 'saving' && (
                              <span className="text-primary-500 text-sm flex items-center gap-1">
                                <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
                              </span>
                            )}
                            {saveStatus[selectedFamily] === 'saved' && (
                              <span className="text-success-500 text-sm flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" /> Guardado
                              </span>
                            )}
                            {saveStatus[selectedFamily] === 'error' && (
                              <span className="text-danger-500 text-sm flex items-center gap-1">
                                <AlertCircle className="w-4 h-4" /> Error
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                        {selectedFamilyData?.has_instructions && (
                          <button
                            onClick={handleDeleteInstructions}
                            disabled={deleteInstructionsMutation.isPending}
                            className="flex items-center gap-2 px-4 py-2 text-danger-600 dark:text-danger-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Eliminar
                          </button>
                        )}
                        <button
                          onClick={handleSaveInstructions}
                          disabled={
                            saveInstructionsMutation.isPending ||
                            !(editedInstructions[selectedFamily]?.trim())
                          }
                          className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          Guardar Instrucciones
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stock Minimum Tab Content */}
                {activeMainTab === 'stock' && (
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <Package className="w-6 h-6 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                          <div>
                            <h3 className="font-semibold text-orange-800 dark:text-orange-200">Stock Minimo</h3>
                            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                              Configura el numero de semanas de stock fisico que debes tener en el almacen
                              para los productos de esta familia.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="max-w-md">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Semanas de stock para la familia <span className="font-mono text-purple-600">{selectedFamily}</span>
                        </label>
                        <div className="flex items-center gap-4">
                          <div className="relative flex-1">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={familyStockWeeks[selectedFamily] ?? ''}
                              onChange={(e) =>
                                setFamilyStockWeeks(prev => ({
                                  ...prev,
                                  [selectedFamily]: parseInt(e.target.value) || 0
                                }))
                              }
                              placeholder="0"
                              className="w-full px-4 py-3 text-lg border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                          </div>
                          <span className="text-slate-600 dark:text-slate-400 font-medium">semanas</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Este valor se utilizara para calcular el stock minimo necesario basandose en el historico de ventas.
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-2">
                          {stockSaveStatus[selectedFamily] === 'saving' && (
                            <span className="text-primary-500 text-sm flex items-center gap-1">
                              <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
                            </span>
                          )}
                          {stockSaveStatus[selectedFamily] === 'saved' && (
                            <span className="text-success-500 text-sm flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> Guardado
                            </span>
                          )}
                          {stockSaveStatus[selectedFamily] === 'error' && (
                            <span className="text-danger-500 text-sm flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" /> Error
                            </span>
                          )}
                        </div>
                        <button
                          onClick={handleSaveStockWeeks}
                          disabled={saveStockWeeksMutation.isPending}
                          className="flex items-center gap-2 px-6 py-2.5 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          Guardar Stock Minimo
                        </button>
                      </div>

                      {/* Stock Exceptions Section */}
                      <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-orange-500" />
                            Excepciones de Stock
                          </h3>
                          {!showStockExceptionForm && (
                            <button
                              onClick={() => setShowStockExceptionForm(true)}
                              className="flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors font-medium text-sm"
                            >
                              <Plus className="w-4 h-4" />
                              Anadir Excepcion
                            </button>
                          )}
                        </div>

                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                          Las excepciones permiten definir semanas de stock personalizadas para productos especificos, ignorando la configuracion de la familia.
                        </p>

                        {/* Add Stock Exception Form */}
                        {showStockExceptionForm && (
                          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4 mb-4">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-medium text-orange-800 dark:text-orange-200">Nueva Excepcion</h4>
                              <button
                                onClick={resetStockExceptionForm}
                                className="p-1 text-slate-500 hover:text-gray-700 dark:hover:text-slate-300"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>

                            {/* Product Search */}
                            {!selectedStockProduct ? (
                              <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                  Buscar producto en familia {selectedFamily}
                                </label>
                                <div className="relative">
                                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                  <input
                                    type="text"
                                    value={stockExceptionSearch}
                                    onChange={(e) => setStockExceptionSearch(e.target.value)}
                                    placeholder="Escribe al menos 2 caracteres para buscar..."
                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                  />
                                  {stockProductSearchQuery.isLoading && (
                                    <Loader2 className="absolute right-3 top-2.5 w-4 h-4 text-orange-500 animate-spin" />
                                  )}
                                </div>

                                {/* Search Results */}
                                {stockProductSearchQuery.data?.products && stockProductSearchQuery.data.products.length > 0 && (
                                  <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800">
                                    {stockProductSearchQuery.data.products.map((product) => (
                                      <button
                                        key={product.reference}
                                        onClick={() => handleSelectStockProduct(product)}
                                        className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                      >
                                        <span className="font-mono text-xs text-purple-600 dark:text-purple-400">{product.reference}</span>
                                        <span className="ml-2 text-sm text-slate-700 dark:text-slate-300">{product.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {stockExceptionSearch.length >= 2 && stockProductSearchQuery.data?.products?.length === 0 && !stockProductSearchQuery.isLoading && (
                                  <p className="text-sm text-slate-500 dark:text-slate-400 py-2">No se encontraron productos</p>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {/* Selected Product */}
                                <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                                  <div>
                                    <span className="font-mono text-sm text-purple-600 dark:text-purple-400">{selectedStockProduct.reference}</span>
                                    <p className="text-sm text-slate-700 dark:text-slate-300">{selectedStockProduct.name}</p>
                                  </div>
                                  <button
                                    onClick={() => setSelectedStockProduct(null)}
                                    className="p-1 text-slate-400 hover:text-danger-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>

                                {/* Weeks Input */}
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Semanas de stock para este producto
                                  </label>
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={stockExceptionWeeks}
                                      onChange={(e) => setStockExceptionWeeks(parseInt(e.target.value) || 0)}
                                      className="w-32 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                    />
                                    <span className="text-slate-600 dark:text-slate-400">semanas</span>
                                  </div>
                                </div>

                                {/* Save Exception Button */}
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={resetStockExceptionForm}
                                    className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={handleSaveStockException}
                                    disabled={saveStockExceptionMutation.isPending || stockExceptionWeeks <= 0}
                                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
                                  >
                                    {saveStockExceptionMutation.isPending ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Save className="w-4 h-4" />
                                    )}
                                    Guardar Excepcion
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Existing Stock Exceptions List */}
                        {stockExceptionsQuery.data && Object.keys(stockExceptionsQuery.data).length > 0 ? (
                          <div className="space-y-2">
                            {Object.values(stockExceptionsQuery.data).map((exception) => (
                              <div
                                key={exception.reference}
                                className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm text-purple-600 dark:text-purple-400">{exception.reference}</span>
                                    <span className="text-sm text-slate-600 dark:text-slate-400">{exception.product_name}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 text-xs">
                                    <span className="text-orange-600 dark:text-orange-400 font-medium">
                                      {exception.min_stock != null && exception.max_stock != null
                                        ? `Fijado a mano: mín ${exception.min_stock} / máx ${exception.max_stock}`
                                        : `${exception.weeks} semanas de stock`}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeleteStockException(exception.reference)}
                                  disabled={deleteStockExceptionMutation.isPending}
                                  className="p-2 text-red-400 hover:text-danger-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No hay excepciones de stock configuradas para esta familia</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
